import { Button, DateInput, FormControl, FormField, FormItem, FormLabel, Input } from '@kalos/ui';
import { format } from 'date-fns';
import { type FC } from 'react';

import {
  getDefaultMemoSummarySearchFields,
  useMemoSummarySearchFormContext,
} from './MemoSummaryUtils';

export interface MemoSummaryFilterProps {
  datesControlledExternally?: boolean;
  preservedDateRange?: {
    startDate?: string;
    endDate?: string;
  };
  /** Phones: only the search field, on one line, with Clear inside it. */
  compact?: boolean;
}

export const MemoSummaryFilter: FC<MemoSummaryFilterProps> = ({
  datesControlledExternally = false,
  preservedDateRange,
  compact = false,
}) => {
  const form = useMemoSummarySearchFormContext();

  const reset = () => {
    if (datesControlledExternally && preservedDateRange) {
      form.reset(
        getDefaultMemoSummarySearchFields({
          startDate: preservedDateRange.startDate,
          endDate: preservedDateRange.endDate,
          response: '',
        }),
      );
      return;
    }
    const defaultValues = getDefaultMemoSummarySearchFields();
    form.reset(defaultValues);
  };

  // Phones: one search field, with Clear inside it once there's something to clear, like the
  // notes search with Filter inside it.
  if (compact) {
    return (
      <FormField
        control={form.control}
        name="response"
        render={({ field }) => (
          <FormItem className="relative space-y-0">
            <FormControl>
              <Input
                {...field}
                type="search"
                autoComplete="off"
                aria-label="Search reports"
                placeholder="Search reports"
                className="bg-card pr-20"
              />
            </FormControl>
            {field.value && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 text-sm"
                onClick={reset}
              >
                Clear
              </Button>
            )}
          </FormItem>
        )}
      />
    );
  }

  return (
    <div className="space-y-4">
      <form onSubmit={form.handleSubmit(console.log)} className="space-y-4">
        <div
          className={`grid grid-cols-1 gap-4 ${datesControlledExternally ? 'md:grid-cols-1' : 'md:grid-cols-3'}`}
        >
          {!datesControlledExternally && (
            <>
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date</FormLabel>
                    <FormControl>
                      <DateInput
                        {...field}
                        value={field.value ? new Date(field.value) : undefined}
                        onChange={(date) =>
                          field.onChange(date ? format(date, 'yyyy-MM-dd') : undefined)
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date</FormLabel>
                    <FormControl>
                      <DateInput
                        {...field}
                        value={field.value ? new Date(field.value) : undefined}
                        onChange={(date) =>
                          field.onChange(date ? format(date, 'yyyy-MM-dd') : undefined)
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </>
          )}

          <FormField
            control={form.control}
            name="response"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Search Response</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="bg-card"
                    placeholder="Search in report response..."
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end">
          <Button type="reset" variant="outline" onClick={reset}>
            Reset
          </Button>
        </div>
      </form>
    </div>
  );
};
